/**
 * gameService.js — Game CRUD, actions, vs-AI, spectator, reactions.
 * Maps to app/api/routes/games.py + daily.py on the backend.
 */
import { apiClient } from "./api";

export const gameService = {
  // Game lifecycle
  createLocalGame: (playerNames, userId) =>
    apiClient
      .post("/games", { player_names: playerNames, user_id: userId })
      .then((r) => r.data),

  createVsAI: ({ userName, botCount, difficulty }) =>
    apiClient
      .post("/games/vs-ai", {
        user_name: userName,
        bot_count: botCount,
        difficulty,
      })
      .then((r) => r.data),

  getGame: (gameId) => apiClient.get(`/games/${gameId}`).then((r) => r.data),

  // Returns { items: [...], next_cursor: string|null }. Pass `before` from
  // the previous response's `next_cursor` to fetch the next page.
  // `sinceMinutes` filters out games older than the given window —
  // the lobby's "Browse Live" list passes 60 so we don't show stale
  // rooms from 4+ hours ago.
  listGames: ({ limit = 20, before = null, sinceMinutes = null } = {}) => {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    if (before) qs.set("before", before);
    if (sinceMinutes) qs.set("since_minutes", String(sinceMinutes));
    return apiClient.get(`/games?${qs.toString()}`).then((r) => r.data);
  },

  // Turn actions
  // V-5/V-6: caller passes the `state_version` from the last response so the
  // backend can reject duplicate retries / concurrent races with HTTP 409.
  // The action's expected_state_version is REQUIRED by the backend.
  // `suppressErrorToast` on every action call: the game UI handles its own
  // feedback (refetch + buzz in GamePage.doAction, plus the stale-retry
  // below). Without it the GLOBAL axios interceptor toasts the raw error
  // first — players were seeing "stale_state_version" / "concurrent_
  // modification" pop-ups on a routine version race that we recover from
  // transparently.
  sendAction: async (gameId, action, expectedStateVersion) => {
    const opts = { suppressErrorToast: true };
    try {
      const res = await apiClient.post(`/games/${gameId}/action`, {
        ...action,
        expected_state_version: expectedStateVersion,
      }, opts);
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isStale =
        err?.response?.status === 409 &&
        (detail?.error === "stale_state_version" ||
          detail === "stale_state_version");
      if (!isStale) throw err;
      // Refetch the latest state to get the fresh version, then
      // retry the same action exactly once.
      const fresh = await apiClient.get(`/games/${gameId}`, opts);
      const freshVersion = fresh?.data?.state_version;
      const res = await apiClient.post(`/games/${gameId}/action`, {
        ...action,
        expected_state_version: freshVersion,
      }, opts);
      return res.data;
    }
  },

  runBotTurn: (gameId) =>
    apiClient
      .post(`/games/${gameId}/bot-turn`, {}, { suppressErrorToast: true })
      .then((r) => r.data),

  // Pause / resume — pass-and-play only. Backend 403s for
  // online / vs-AI / Daily so the button shouldn't even render
  // there, but the API enforces the rule either way.
  pauseGame: (gameId) =>
    apiClient.post(`/games/${gameId}/pause`).then((r) => r.data),
  resumeGame: (gameId) =>
    apiClient.post(`/games/${gameId}/resume`).then((r) => r.data),

  // Daily Challenge
  createDailyChallenge: ({ botCount, difficulty }) =>
    apiClient
      .post("/daily-challenge", { bot_count: botCount, difficulty })
      .then((r) => r.data),

  // Returns { items: [...], limit, offset, has_more, total }.
  getDailyLeaderboard: ({ limit = 20, offset = 0 } = {}) => {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return apiClient
      .get(`/daily-leaderboard?${qs.toString()}`)
      .then((r) => r.data);
  },

  // Spectator
  getSpectatorState: (gameId) =>
    apiClient.get(`/games/${gameId}/spectator`).then((r) => r.data),

  // Reactions (in-game emoji broadcast).
  // 429 on this route is the per-player cooldown — expected, not an
  // error to toast about (the React component already greys out the
  // bar). Suppress the global toast.
  sendReaction: (gameId, playerIdx, emoji) =>
    apiClient
      .post(
        `/games/${gameId}/reaction`,
        { game_id: gameId, player_idx: playerIdx, emoji },
        { suppressErrorToast: true },
      )
      .then((r) => r.data),
};
